import type { CSSProperties, ReactNode } from 'react';
import { motion } from 'motion/react';
import { cn } from './util';

export type BubbleTail =
  | 'bottom-left'
  | 'bottom'
  | 'bottom-right'
  | 'top-left'
  | 'top'
  | 'top-right'
  | 'left'
  | 'right'
  | 'none';

export interface SpeechBubbleProps {
  children: ReactNode;
  tail?: BubbleTail;
  tone?: 'white' | 'yellow' | 'blue' | 'red';
  size?: 'sm' | 'md' | 'lg';
  /** Pop-in animation on mount (re-mount with a new `key` to pop again). */
  pop?: boolean;
  /** "Shout" bubble: jagged outline for exclamations. */
  shout?: boolean;
  className?: string;
  style?: CSSProperties;
}

const FILL = { white: '#ffffff', yellow: '#ffde00', blue: '#dbe7ff', red: '#ffe0d9' } as const;
const SIZE = {
  sm: 'px-3 py-1 text-[13px]',
  md: 'px-4 py-1.5 text-[16px]',
  lg: 'px-5 py-2.5 text-[21px]',
} as const;

/**
 * Tail geometry: an open "V" path whose fill covers the bubble border where it joins.
 * Rendered pointing down, then rotated for the other directions.
 */
function Tail({ tail, fill }: { tail: Exclude<BubbleTail, 'none'>; fill: string }) {
  const pos: Record<Exclude<BubbleTail, 'none'>, string> = {
    'bottom-left': 'left-5 top-full',
    bottom: 'left-1/2 -translate-x-1/2 top-full',
    'bottom-right': 'right-5 top-full',
    'top-left': 'left-5 bottom-full rotate-180',
    top: 'left-1/2 -translate-x-1/2 bottom-full rotate-180',
    'top-right': 'right-5 bottom-full rotate-180',
    left: 'right-full top-1/2 -translate-y-1/2 -mr-[3px] rotate-90',
    right: 'left-full top-1/2 -translate-y-1/2 -ml-[3px] -rotate-90',
  };
  const skew = tail.endsWith('left')
    ? 'M2 0 L6 16 L22 0'
    : tail.endsWith('right')
      ? 'M2 0 L18 16 L22 0'
      : 'M2 0 L12 16 L22 0';
  return (
    <svg
      aria-hidden
      width="24"
      height="18"
      viewBox="0 0 24 18"
      className={cn('absolute overflow-visible', pos[tail])}
    >
      <path d={skew} fill={fill} stroke="#111" strokeWidth="3" strokeLinejoin="round" />
      <rect x="3.6" y="-0.6" width="16.8" height="3.7" fill={fill} />
    </svg>
  );
}

/**
 * Comic speech bubble. Carries its own top margin (and bottom margin for bottom tails) so it never
 * touches neighbouring controls.
 */
export function SpeechBubble({
  children,
  tail = 'bottom-left',
  tone = 'white',
  size = 'md',
  pop = true,
  shout,
  className,
  style,
}: SpeechBubbleProps) {
  const fill = FILL[tone];
  const isTop = tail.startsWith('top');
  const isBottom = tail.startsWith('bottom');
  return (
    <motion.div
      initial={pop ? { scale: 0.3, opacity: 0, rotate: -6 } : false}
      animate={{ scale: 1, opacity: 1, rotate: shout ? -2 : 0 }}
      transition={{ type: 'spring', stiffness: 560, damping: 17 }}
      className={cn(
        'relative inline-block w-fit max-w-full border-3 border-ink font-black leading-tight text-ink shadow-ink-xs',
        shout ? 'rounded-[6px]' : 'rounded-[18px]',
        SIZE[size],
        isTop ? 'mt-6' : 'mt-3',
        isBottom && 'mb-4',
        className,
      )}
      style={{ backgroundColor: fill, transformOrigin: isTop ? '50% 0%' : '20% 100%', ...style }}
    >
      {children}
      {tail !== 'none' && <Tail tail={tail} fill={fill} />}
    </motion.div>
  );
}
